import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles,
  Target,
  Route,
  Compass,
  Shield,
  Briefcase,
  Building2,
  ArrowRight,
  CheckCircle,
  Clock,
  Users,
  TrendingUp,
  Star,
  Zap,
  Play,
  ChevronRight,
  Award,
  Rocket
} from 'lucide-react';

const InnovationCenter = () => {
  // Featured main section - Product AI Roadmap
  const mainSection = {
    icon: Route,
    title: "Product AI Roadmap",
    description: "AI-powered strategic planning for breakthrough product development with intelligent milestone tracking and comprehensive roadmap visualization.",
    features: [
      "Strategic planning & forecasting",
      "Milestone tracking & optimization", 
      "Resource allocation & management",
      "Timeline optimization & analysis"
    ],
    color: "from-blue-500 to-cyan-500",
    path: "/roadmap-innovation",
    stats: { projects: "634", success: "89%", users: "2.3k" }
  };

  // Other sections - smaller cards
  const otherSections = [
    {
      icon: Sparkles,
      title: "AI Ideation",
      description: "Unlock creative potential with AI-driven brainstorming for breakthrough concepts",
      color: "from-purple-500 to-pink-500",
      badge: "AI",
      path: "/ideation",
      stats: { projects: "1.2k", success: "94%" }
    },
    {
      icon: Target,
      title: "TRIZ Solver",
      description: "Systematic innovation using 40 proven inventive principles",
      color: "from-indigo-500 to-blue-500",
      badge: null,
      path: "/problem-solving",
      stats: { projects: "856", success: "97%" }
    },
    {
      icon: Compass,
      title: "Market Discovery",
      description: "Identify untapped opportunities with comprehensive market analysis",
      color: "from-green-500 to-emerald-500",
      badge: "New",
      path: "/market-discovery",
      stats: { projects: "423", success: "91%" }
    },
    {
      icon: Shield,
      title: "IP Strategy",
      description: "Strategic patent development and intellectual property protection",
      color: "from-orange-500 to-red-500",
      badge: null,
      path: "/ip-strategy",
      stats: { projects: "312", success: "96%" }
    },
    {
      icon: Briefcase,
      title: "Business Models",
      description: "Design winning strategies with value proposition frameworks",
      color: "from-cyan-500 to-teal-500",
      badge: null,
      path: "/business-model",
      stats: { projects: "267", success: "88%" }
    },
    {
      icon: Building2,
      title: "Architecture",
      description: "Engineer scalable systems with advanced architectural patterns",
      color: "from-slate-500 to-gray-600",
      badge: "Beta",
      path: "/algorithm-optimization",
      stats: { projects: "89", success: "85%" }
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white/90 dark:bg-gray-950/90 backdrop-blur-xl border-b border-gray-100 dark:border-gray-900 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl sqsft-heading-1 mb-3">
                Innovation Center
              </h1>
              <p className="text-lg sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
                Choose your approach to systematic innovation
              </p>
            </div>
            
            <Link to="/home" className="sqsft-nav-item">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Featured Main Section - Product AI Roadmap */}
        <section className="pt-32 pb-24 px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="sqsft-badge sqsft-badge-gold mb-8">
              <Zap className="w-4 h-4 mr-2" />
              Recommended Approach
            </div>
            <h2 className="text-5xl lg:text-6xl sqsft-heading-1 mb-8 leading-tight">
              Product AI
              <br />
              <span className="sqsft-gradient-text">Roadmap</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              {mainSection.description}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="group relative"
          >
            <div className={`absolute inset-0 bg-gradient-to-b ${mainSection.color.replace('500', '500/10')} rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
            <div className="relative bg-white dark:bg-gray-900 rounded-3xl p-12 border border-gray-100 dark:border-gray-800 hover:border-gray-200 dark:hover:border-gray-700 transition-all duration-300 group-hover:shadow-2xl group-hover:-translate-y-2">
              {/* Header */}
              <div className="flex flex-col lg:flex-row items-center justify-between mb-12">
                <div className="flex items-center space-x-6 mb-8 lg:mb-0">
                  <div className="relative">
                    <div className={`w-20 h-20 bg-gradient-to-br ${mainSection.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <Route className="w-10 h-10 text-white" />
                    </div>
                    <div className={`absolute inset-0 bg-gradient-to-br ${mainSection.color} rounded-2xl opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-300`}></div>
                  </div>
                  
                  <div>
                    <div className="flex items-center space-x-8 text-sm text-gray-500 dark:text-gray-400 mb-2">
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4" />
                        <span>{mainSection.stats.users} users</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Rocket className="w-4 h-4" />
                        <span>{mainSection.stats.projects} projects</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="w-4 h-4" />
                        <span>{mainSection.stats.success} success</span>
                      </div>
                    </div>
                    <h3 className="text-2xl font-semibold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300">
                      Most comprehensive approach
                    </h3>
                  </div>
                </div>

                <Link 
                  to={mainSection.path}
                  className={`px-8 py-4 bg-gradient-to-r ${mainSection.color} text-white font-semibold rounded-2xl hover:shadow-lg transition-all duration-300 flex items-center space-x-3 group-hover:scale-105`}
                >
                  <span>Start Roadmap</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {mainSection.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl group-hover:bg-gray-100 dark:group-hover:bg-gray-800 transition-colors duration-300">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        {/* Other Innovation Approaches */}
        <section className="py-32 px-8 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900/50 dark:to-gray-950">
          <div className="text-center mb-24">
            <div className="inline-flex items-center px-4 py-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-full text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-8">
              <Target className="w-4 h-4 mr-2" />
              Specialized Tools
            </div>
            <h3 className="text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8 leading-tight">
              Innovation Made
              <br />
              <span className="gradient-text">Focused</span>
            </h3>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Powerful specialized solutions for specific innovation challenges and methodologies
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherSections.map((section, index) => {
              const Icon = section.icon;
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                  className="group relative"
                >
                  <div className={`absolute inset-0 bg-gradient-to-b ${section.color.replace('500', '500/10')} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                  <div className="relative bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-100 dark:border-gray-800 hover:border-gray-200 dark:hover:border-gray-700 transition-all duration-300 group-hover:shadow-2xl group-hover:-translate-y-2 h-full">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="relative">
                        <div className={`w-16 h-16 bg-gradient-to-br ${section.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <div className={`absolute inset-0 bg-gradient-to-br ${section.color} rounded-2xl opacity-20 blur-xl group-hover:opacity-40 transition-opacity duration-300`}></div>
                      </div>
                      {section.badge && (
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                          section.badge === 'AI' 
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                            : section.badge === 'New'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        }`}>
                          {section.badge}
                        </span>
                      )}
                    </div>

                    <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300">
                      {section.title}
                    </h4>
                    
                    <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                      {section.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          <CheckCircle className="w-4 h-4 inline mr-1 text-green-500" />
                          Ready to use
                        </div>
                      </div>
                      <Link to={section.path} className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors group-hover:translate-x-1">
                        <ChevronRight className="w-5 h-5" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <div className="inline-flex items-center space-x-6">
              <Link to="/roadmap-innovation" className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold rounded-2xl hover:from-indigo-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl">
                Start Your Innovation Journey
              </Link>
              <button className="px-8 py-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-semibold transition-colors duration-300 flex items-center space-x-2">
                <Play className="w-5 h-5" />
                <span>Watch Demo</span>
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default InnovationCenter; 